import { useState, useEffect } from 'react';

/**
 * ContactDetailModal - Edit contact nickname and notes
 * Shows contact info, allows editing of private nickname and notes
 */
export default function ContactDetailModal({ contact, onClose, onUpdate, toast }) {
  const [nickname, setNickname] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with current values
  useEffect(() => {
    setNickname(contact.nickname || '');
    setNotes(contact.notes || '');
    setHasChanges(false);
  }, [contact]);

  // Track changes
  const handleNicknameChange = (value) => {
    setNickname(value);
    setHasChanges(true);
  };

  const handleNotesChange = (value) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(contact.contactId, {
        nickname: nickname.trim(),
        notes: notes.trim()
      });
      toast.success('Contact updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1c1c1e] rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#1c1c1e] border-b border-[#d2d2d7] dark:border-[#2c2c2e] p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">
            Edit Contact
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] rounded-full transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Contact Header */}
          <div className="flex items-center gap-4 pb-4 border-b border-[#d2d2d7] dark:border-[#2c2c2e]">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#06c] to-[#0088cc] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">
                {(contact.nickname || contact.contactName)?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1d1d1f] dark:text-white">
                {contact.nickname || contact.contactName || 'Unknown'}
              </p>
              <p className="text-sm text-[#86868b] dark:text-[#8e8e93]">
                {contact.contactName && contact.nickname && contact.contactName}
              </p>
            </div>
          </div>

          {/* Nickname Field */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Nickname
              <span className="text-xs text-[#86868b] dark:text-[#8e8e93] font-normal ml-1">(Private)</span>
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => handleNicknameChange(e.target.value)}
              placeholder={contact.contactName || 'Enter a nickname'}
              maxLength="50"
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-[#d2d2d7] dark:border-[#3a3a3c] rounded-xl focus:outline-none focus:border-[#06c] dark:focus:border-[#0088cc] transition-colors text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93]"
            />
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              {nickname.length}/50
            </p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              Only you can see this nickname
            </p>
          </div>

          {/* Notes Field */}
          <div>
            <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
              Notes
              <span className="text-xs text-[#86868b] dark:text-[#8e8e93] font-normal ml-1">(Private)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Add notes about this contact (where you met, etc.)"
              maxLength="500"
              rows="4"
              className="w-full px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-[#d2d2d7] dark:border-[#3a3a3c] rounded-xl focus:outline-none focus:border-[#06c] dark:focus:border-[#0088cc] transition-colors text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] resize-none"
            />
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              {notes.length}/500
            </p>
            <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mt-1">
              Only you can see these notes
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-[#1c1c1e] border-t border-[#d2d2d7] dark:border-[#2c2c2e] p-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-[#1d1d1f] dark:text-white bg-[#f5f5f7] dark:bg-[#2c2c2e] hover:bg-[#e5e5ea] dark:hover:bg-[#424245] rounded-xl font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex-1 px-4 py-2.5 text-white bg-gradient-to-r from-[#06c] to-[#0055d4] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium transition-all"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
